import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useProductionOptimization } from '@/hooks/useProductionOptimization';
import { 
  Zap, 
  Settings, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Target
} from 'lucide-react';
import { toast } from 'sonner';

export function EnhancedProductionOptimizer() {
  const {
    settings,
    metrics,
    successCriteriaStatus,
    isOptimizing,
    optimizationProgress,
    updateSettings,
    runFullOptimization,
    optimizeCache,
    optimizeCosts,
    optimizePerformance,
    fixMimeErrors,
    isOptimizingCache,
    isOptimizingCosts,
    isOptimizingPerformance,
    isFixingMimeErrors
  } = useProductionOptimization();

  const [autoOptimization, setAutoOptimization] = useState(false);

  // Auto-optimization interval
  useEffect(() => {
    if (autoOptimization) {
      const interval = setInterval(() => {
        runFullOptimization();
      }, 5 * 60 * 1000); // Every 5 minutes

      return () => clearInterval(interval);
    }
  }, [autoOptimization, runFullOptimization]);

  // Auto-fix MIME errors when detected
  useEffect(() => {
    if (successCriteriaStatus && !successCriteriaStatus.mimeTypeErrors) {
      fixMimeErrors();
    }
  }, [successCriteriaStatus, fixMimeErrors]);

  const handleSettingChange = (category: string, setting: string, value: any) => {
    updateSettings({
      [category]: {
        ...settings[category as keyof typeof settings],
        [setting]: value
      }
    });
  };

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="w-5 h-5 text-green-600" />
    ) : (
      <AlertTriangle className="w-5 h-5 text-red-600" />
    );
  };

  const getSuccessCriteriaColor = (status: boolean) => {
    return status ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Enhanced Production Optimizer</h2>
          <p className="text-muted-foreground">AI-powered optimization for Phase 5 success criteria</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center space-x-2">
            <label htmlFor="auto-optimize" className="text-sm font-medium">
              Auto-Optimize
            </label>
            <Switch
              id="auto-optimize"
              checked={autoOptimization}
              onCheckedChange={setAutoOptimization}
            />
          </div>
          <Button 
            onClick={runFullOptimization} 
            disabled={isOptimizing}
            className="flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            {isOptimizing ? `Optimizing... ${optimizationProgress}%` : 'Optimize All'}
          </Button>
        </div>
      </div>

      {isOptimizing && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Optimization Progress</span>
                <span>{optimizationProgress}%</span>
              </div>
              <Progress value={optimizationProgress} className="w-full" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Criteria Status */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Phase 5 Success Criteria
          </CardTitle>
          <CardDescription>
            Track progress towards production optimization goals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className={`p-3 rounded-lg border-2 ${getSuccessCriteriaColor(successCriteriaStatus?.mimeTypeErrors || false)}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">Zero MIME Errors</span>
                {getStatusIcon(successCriteriaStatus?.mimeTypeErrors || false)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Current: {metrics?.mimeTypeErrors || 0} errors
              </p>
            </div>
            
            <div className={`p-3 rounded-lg border-2 ${getSuccessCriteriaColor(successCriteriaStatus?.costReduction || false)}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">90% Cost Reduction</span>
                {getStatusIcon(successCriteriaStatus?.costReduction || false)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Current: {metrics?.costReduction || 0}%
              </p>
            </div>
            
            <div className={`p-3 rounded-lg border-2 ${getSuccessCriteriaColor(successCriteriaStatus?.successRate || false)}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">99%+ Success Rate</span>
                {getStatusIcon(successCriteriaStatus?.successRate || false)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Current: {metrics?.successRate || 0}%
              </p>
            </div>
            
            <div className={`p-3 rounded-lg border-2 ${getSuccessCriteriaColor(successCriteriaStatus?.processingTime || false)}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">&lt;15s Processing</span>
                {getStatusIcon(successCriteriaStatus?.processingTime || false)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Current: {metrics?.averageProcessingTime || 0}s
              </p>
            </div>
            
            <div className={`p-3 rounded-lg border-2 ${getSuccessCriteriaColor(successCriteriaStatus?.utilitySupport || false)}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">50+ Utilities</span>
                {getStatusIcon(successCriteriaStatus?.utilitySupport || false)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Current: {metrics?.utilityCompaniesSupported || 0} companies
              </p>
            </div>
            
            <div className={`p-3 rounded-lg border-2 ${getSuccessCriteriaColor(successCriteriaStatus?.dataAccuracy || false)}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">95%+ Accuracy</span>
                {getStatusIcon(successCriteriaStatus?.dataAccuracy || false)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Current: {metrics?.dataAccuracy || 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="quick-actions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="quick-actions">Quick Actions</TabsTrigger>
          <TabsTrigger value="cache-settings">Cache Settings</TabsTrigger>
          <TabsTrigger value="cost-settings">Cost Settings</TabsTrigger>
          <TabsTrigger value="performance-settings">Performance Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="quick-actions" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Optimize Cache
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => optimizeCache()} 
                  disabled={isOptimizingCache}
                  className="w-full"
                  variant="outline"
                >
                  {isOptimizingCache ? 'Optimizing...' : 'Optimize Cache'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Reduce Costs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => optimizeCosts()} 
                  disabled={isOptimizingCosts}
                  className="w-full"
                  variant="outline"
                >
                  {isOptimizingCosts ? 'Optimizing...' : 'Optimize Costs'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Boost Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => optimizePerformance()} 
                  disabled={isOptimizingPerformance}
                  className="w-full"
                  variant="outline"
                >
                  {isOptimizingPerformance ? 'Optimizing...' : 'Optimize Performance'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Fix MIME Errors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => fixMimeErrors()} 
                  disabled={isFixingMimeErrors}
                  className="w-full"
                  variant="outline"
                >
                  {isFixingMimeErrors ? 'Fixing...' : 'Fix Errors'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="cache-settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cache Optimization Settings</CardTitle>
              <CardDescription>Configure caching strategy for optimal performance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Enable Caching</label>
                <Switch
                  checked={settings.cache.enabled}
                  onCheckedChange={(checked) => handleSettingChange('cache', 'enabled', checked)}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Cache Strategy</label>
                <select 
                  value={settings.cache.strategy}
                  onChange={(e) => handleSettingChange('cache', 'strategy', e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="aggressive">Aggressive (Maximum Performance)</option>
                  <option value="balanced">Balanced (Recommended)</option>
                  <option value="conservative">Conservative (Minimal Memory)</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">TTL (seconds): {settings.cache.ttl}</label>
                <Slider
                  value={[settings.cache.ttl]}
                  onValueChange={([value]) => handleSettingChange('cache', 'ttl', value)}
                  min={300}
                  max={7200}
                  step={300}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Max Cache Size: {settings.cache.maxSize}</label>
                <Slider
                  value={[settings.cache.maxSize]}
                  onValueChange={([value]) => handleSettingChange('cache', 'maxSize', value)}
                  min={100}
                  max={5000}
                  step={100}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cost-settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cost Optimization Settings</CardTitle>
              <CardDescription>Configure AI model usage and cost thresholds</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Budget Limit ($): {settings.costs.budgetLimit}</label>
                <Slider
                  value={[settings.costs.budgetLimit]}
                  onValueChange={([value]) => handleSettingChange('costs', 'budgetLimit', value)}
                  min={10}
                  max={1000}
                  step={10}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Cost Threshold ($): {settings.costs.costThreshold}</label>
                <Slider
                  value={[settings.costs.costThreshold]}
                  onValueChange={([value]) => handleSettingChange('costs', 'costThreshold', value)}
                  min={0.001}
                  max={0.1}
                  step={0.001}
                  className="w-full"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Optimize Prompts</label>
                <Switch
                  checked={settings.costs.optimizePrompts}
                  onCheckedChange={(checked) => handleSettingChange('costs', 'optimizePrompts', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance-settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Settings</CardTitle>
              <CardDescription>Configure processing performance parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Target Processing Time (s): {settings.performance.targetProcessingTime}</label>
                <Slider
                  value={[settings.performance.targetProcessingTime]}
                  onValueChange={([value]) => handleSettingChange('performance', 'targetProcessingTime', value)}
                  min={5}
                  max={30}
                  step={1}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Batch Size: {settings.performance.batchSize}</label>
                <Slider
                  value={[settings.performance.batchSize]}
                  onValueChange={([value]) => handleSettingChange('performance', 'batchSize', value)}
                  min={1}
                  max={20}
                  step={1}
                  className="w-full"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Parallel Processing</label>
                <Switch
                  checked={settings.performance.parallelProcessing}
                  onCheckedChange={(checked) => handleSettingChange('performance', 'parallelProcessing', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Priority Queue</label>
                <Switch
                  checked={settings.performance.priorityQueue}
                  onCheckedChange={(checked) => handleSettingChange('performance', 'priorityQueue', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}